// src/pages/tickets/TicketLists.jsx
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Chip,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { ticketListColumns } from '../../component/common/tableHelper';
import useApiService from '../../component/hooks/useApiService';
import Filter from '../../component/ui/filter/filter';
import { useDialog } from '../../context/dialog/DialogContext';
import { useAppSnackbar } from '../../component/providers/SnackbarProvider';
import DataGrid from '../../component/ui/data-grid/DataGrid';
import { BOOST_CATEGORY_TYPES, TICKET_STATE_TYPES } from '../../constans/parameter';
import { apiUrls } from '../../constans/apiUrls';
import TicketDetailPage from './TicketDetailPage';
import UserBoostsAssignmentsList from '../manage-boosts/user/UserBoostsAssignmentsList';
import TicketStatePage from './TicketStatePage';
import '../../App.css';

const TicketLists = () => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ state: ['open', 'approved'] });
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { executeGet } = useApiService();
  const { openDialog } = useDialog();
  const { showSnackbar } = useAppSnackbar();

  const [boostCategories, setBoostCategories] = useState([]);

  const columns = useMemo(() => ticketListColumns(), []);

  // Measure actual container width (accounts for sidebar and zoom)
  const gridContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1920);
  const baseDprRef = useRef(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
  const [zoomScale, setZoomScale] = useState(() => (typeof window !== 'undefined' ? (window.visualViewport?.scale || (window.devicePixelRatio || 1) / (window.devicePixelRatio || 1)) : 1));
  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect?.width || el.clientWidth || 0;
        setContainerWidth(w);
      }
    });
    ro.observe(el);
    const handleViewportResize = () => {
      const vv = window.visualViewport?.scale;
      const dpr = window.devicePixelRatio || 1;
      const base = baseDprRef.current || 1;
      const fallback = dpr / base;
      setZoomScale(vv || fallback || 1);
    };
    window.visualViewport?.addEventListener('resize', handleViewportResize);
    window.addEventListener('resize', handleViewportResize);
    return () => {
      ro.disconnect();
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
      window.removeEventListener('resize', handleViewportResize);
    };
  }, []);

  const columnVisibilityModel = useMemo(() => {
    const w = Math.max(0, (containerWidth || 0) / (zoomScale || 1) - 64); // adjust with zoom and padding
    const model = {};

    // approximate minimal required widths (px) of visible columns
    let required = 0;
    const widths = {
      user_email: 200,
      category_code: 150,
      bucket: 120,
      requested_qty: 140,
      user_note: 220,
      created_at: 170,
      last_message_at: 170,
      state: 120,
      actions: 152,
    };

    required = widths.user_email + widths.category_code + widths.bucket + widths.requested_qty + widths.user_note + widths.created_at + widths.last_message_at + widths.state + widths.actions;

    if (w < required) { model.last_message_at = false; required -= widths.last_message_at; }
    if (w < required) { model.created_at = false;      required -= widths.created_at; }
    if (w < required) { model.user_note = false;       required -= widths.user_note; }
    if (w < required) { model.category_code = false;   required -= widths.category_code; }
    if (w < required) { model.bucket = false;          required -= widths.bucket; }
    if (w < required) { model.state = false;           required -= widths.state; }
    if (w < required) { model.requested_qty = false;   required -= widths.requested_qty; }

    return model;
  }, [containerWidth, zoomScale]);

  const fetchData = async (
    filterParams = filter,
    page = paginationModel.page,
    pageSize = paginationModel.pageSize
  ) => {
    setLoading(true);
    try {
      const params = {
        ...filterParams,
        state: Array.isArray(filterParams.state)
          ? filterParams.state.join(',')
          : filterParams.state,
        offset: page * pageSize,
        limit: pageSize,
      };

      const response = await executeGet({
        url: apiUrls.BoostTickets,
        params,
      });

      setRows(response.items ?? []);
      setTotalCount(response.meta?.total ?? 0);
    } catch (err) {
      console.error('Veri alınamadı:', err);
      setError('Veri yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(filter, paginationModel.page, paginationModel.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleReset = () => {
    const emptyFilter = { state: ['open', 'approved'] };
    setFilter(emptyFilter);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    if (paginationModel.page === 0) {
      fetchData(emptyFilter, 0);
    }
  };

  useEffect(() => {
    getBoostCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBoostCategories = async () => {
    await executeGet({
      url: apiUrls.BoostCategories,
      setStateDelegate: setBoostCategories,
    });
  };

  const onBoostUsageClicked = useCallback(
    (data) => {
      openDialog({
        title: 'Atanmış Boost Kullandırma',
        MaxWidth: 'xl',
        content: <UserBoostsAssignmentsList data={data} openType={'Usage'} />,
        callback: (res) => {
          if (res?.id) {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            fetchData();
            showSnackbar('Boost kullanıldı.', 'info');
          }
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter]
  );

  const onTicketApproveClicked = useCallback(
    (data) => {
      const ConfirmContent = ({ close }) => (
        <Box>
          <Typography gutterBottom>Ticket onaylanacaktır. Onaylıyor musunuz?</Typography>
          <Typography variant="body2" color="warning.main" gutterBottom>
            Not: Durum değişikliği geri alınamayacaktır.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button variant="text" onClick={() => close(false)}>Vazgeç</Button>
            <Button
              variant="contained"
              onClick={() => {
                close(true);
                openDialog({
                  title: 'Ticket Onaylama',
                  maxWidth: 'sm',
                  content: <TicketStatePage data={data} openType={'Approve'} />,
                  callback: (res) => {
                    if (res === true) {
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                      fetchData();
                      showSnackbar('Ticket onaylandı.', 'info');
                    }
                  },
                });
              }}
            >
              Onayla
            </Button>
          </Box>
        </Box>
      );
      openDialog({ title: 'Onay', maxWidth: 'xs', content: <ConfirmContent /> });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter]
  );

  const onTicketRejectClicked = useCallback(
    (data) => {
      const ConfirmContent = ({ close }) => (
        <Box>
          <Typography gutterBottom>Ticket reddedilecektir. Onaylıyor musunuz?</Typography>
          <Typography variant="body2" color="warning.main" gutterBottom>
            Not: Durum değişikliği geri alınamayacaktır.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button variant="text" onClick={() => close(false)}>Vazgeç</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                close(true);
                openDialog({
                  title: 'Ticket Reddetme',
                  maxWidth: 'sm',
                  content: <TicketStatePage data={data} openType={'Reject'} />,
                  callback: (res) => {
                    if (res === true) {
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                      fetchData();
                      showSnackbar('Ticket reddedildi.', 'info');
                    }
                  },
                });
              }}
            >
              Onayla
            </Button>
          </Box>
        </Box>
      );
      openDialog({ title: 'Onay', maxWidth: 'xs', content: <ConfirmContent /> });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter]
  );

  const onTicketCompleteClicked = useCallback(
    (data) => {
      const ConfirmContent = ({ close }) => (
        <Box>
          <Typography gutterBottom>Ticket tamamlanacaktır. Onaylıyor musunuz?</Typography>
          <Typography variant="body2" color="warning.main" gutterBottom>
            Not: Durum değişikliği geri alınamayacaktır.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button variant="text" onClick={() => close(false)}>Vazgeç</Button>
            <Button
              variant="contained"
              onClick={() => {
                close(true);
                openDialog({
                  title: 'Ticket Tamamlama',
                  maxWidth: 'sm',
                  content: <TicketStatePage data={data} openType={'Complete'} />,
                  callback: (res) => {
                    if (res === true) {
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                      fetchData();
                      showSnackbar('Ticket tamamlandı.', 'info');
                    }
                  },
                });
              }}
            >
              Onayla
            </Button>
          </Box>
        </Box>
      );
      openDialog({ title: 'Onay', maxWidth: 'xs', content: <ConfirmContent /> });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter]
  );
  const onViewChatClicked = useCallback(
    (data) => {
      let openTypeState = 'View';
      if (data.state === 'approved' || data.state === 'open') openTypeState = 'Chat';

      openDialog({
        title: 'Kullanıcı Mesajı',
        maxWidth: 'sm',
        content: <TicketDetailPage openType={openTypeState} id={data.id} />,
        callback: () => {
          fetchData(filter);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter]
  );

  const onBoostViewClicked = useCallback(
    (data) => {
      openDialog({
        title: 'Atanmış Boost Detayı',
        MaxWidth: 'xl',
        content: <UserBoostsAssignmentsList data={data} openType={'AssignmentDetail'} />,
        callback: () => { },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter]
  );

  const rowActions = useMemo(
    () => [
      { name: 'view', icon: 'view', tooltip: 'Görüntüle', onClick: onBoostViewClicked },
      { name: 'chatUser', icon: 'chatIcon', tooltip: 'Kullanıcı ile Sohbet', onClick: onViewChatClicked },
      { name: 'approveTicket', icon: 'approval', tooltip: 'Ticket Onayla', onClick: onTicketApproveClicked },
      { name: 'rejectTicket', icon: 'cancel', tooltip: 'Ticket Reddet', onClick: onTicketRejectClicked },
      { name: 'completeTicket', icon: 'doneIcon', tooltip: 'Ticket Tamamlama', onClick: onTicketCompleteClicked },
      { name: 'usageBoost', icon: 'remove', tooltip: 'Boost Kullan', onClick: onBoostUsageClicked },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter]
  );

  const onRowActionVisible = (row, action) => {
    switch (action) {
      case 'view':
        return true;
      case 'usageBoost':
        return row.state === 'approved';
      case 'completeTicket':
        return row.state === 'approved';
      case 'rejectTicket':
        return row.state === 'open';
      case 'approveTicket':
        return row.state === 'open';
      case 'chatUser':
        return true;
      default:
        return false;
    }
  };

  return (
    <div style={{ padding: '0 1rem 1rem' }}>
      <h2>Ticket Listesi</h2>
      {error && <div style={{ color: 'red' }}>{error}</div>}

      <Filter onFilter={() => fetchData(filter)} onFilterReset={handleReset}>
        <Grid item xs={4}>
          <TextField
            fullWidth
            name="Email"
            label="Email"
            variant="outlined"
            size="small"
            value={filter?.email || ''}
            onChange={(e) => setFilter({ ...filter, email: e.target.value })}
          />
        </Grid>

        <Grid item xs={4}>
          <FormControl size="small" sx={{ minWidth: 220 }} variant="outlined">
            <InputLabel id="boost-label">Boost Kategorisi</InputLabel>
            <Select
              labelId="boost-type-label"
              id="boost-type"
              value={filter?.category || ''}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              label="Boost Kategorisi"
            >
              {boostCategories.map((type) => (
                <MenuItem key={type.code} value={type.code}>
                  {type.name_tr || type.name || type.name_en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={4}>
          <FormControl size="small" sx={{ minWidth: 220 }} variant="outlined">
            <InputLabel id="boost-category-label">Boost Tipi</InputLabel>
            <Select
              labelId="boost-categoty-type-label"
              id="boost-category-type"
              value={filter?.bucket || ''}
              onChange={(e) => setFilter({ ...filter, bucket: e.target.value })}
              label="Boost Tipi"
            >
              {BOOST_CATEGORY_TYPES.map((type) => (
                <MenuItem key={type.Code} value={type.Code}>
                  {type.ValueText1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={4}>
          <FormControl size="small" sx={{ minWidth: 220 }} variant="outlined">
            <InputLabel id="ticket-state-label">Durum</InputLabel>
            <Select
              labelId="ticket-state-label"
              id="ticket-state-type"
              multiple
              value={filter?.state ?? []}
              onChange={(e) => {
                const value = typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value;
                setFilter({ ...filter, state: value });
              }}
              label="Durum"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden' }}>
                  {(selected || []).map((code) => {
                    const item = TICKET_STATE_TYPES.find((t) => t.Code === code);
                    return <Chip key={code} size="small" label={item?.ValueText1 ?? code} />;
                  })}
                </Box>
              )}
            >
              {TICKET_STATE_TYPES.map((type) => {
                const checked = (filter?.state || []).includes(type.Code);
                return (
                  <MenuItem key={type.Code} value={type.Code}>
                    <Checkbox size="small" checked={checked} />
                    <ListItemText primary={type.ValueText1} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      </Filter>

      <div ref={gridContainerRef} style={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          pagination
          rowActions={rowActions}
          onRowActionVisible={onRowActionVisible}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          rowCount={totalCount}
          paginationMode="server"
          loading={loading}
          getRowClassName={(params) => (params.row?.unread ? 'unread-row' : '')}
          getRowId={(row) => row.id ?? row.ticket_id ?? `${row.user_email}-${row.created_at}`}
          onRowSelect={(d) => {
          }}
        />
      </div>
    </div>
  );
};

export default TicketLists;
